import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Segment } from "semantic-ui-react";
import _ from "lodash";
import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { BOX_LOWER_FILL_COLOR, BOX_UPPER_FILL_COLOR, TRANSPARENT_COLOR } from "../../../../utilities/graphics";
import type { AucTableData } from "../../../../apis/types/analysisTypes";
import type { Annotations, Layout, ModeBarButtonAny } from "plotly.js";
import "./style.css";
import { PlotlyIcons } from "./plotlyIcons";
import { useTrialAnalysis } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import { AXIS_TICK_SIZE, AXIS_TITLE_SIZE, CHART_TITLE_SIZE } from "./constants";
import { getColorFromPalette } from "../../Trials/MFASummary/MapStyles";

type Props = {
	aucData?: AucTableData[];
	analysisName?: string;
	treatmentNames?: Record<number, string>;
	aucMeanComparisons?: {
		cv: string;
		mean: string;
		pValue: string;
		letteringPairs: { treatmentId: number; letter: string }[];
	};
};

const TimecourseBoxWhiskerChart = ({ aucData, analysisName = "", treatmentNames, aucMeanComparisons }: Props) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);
	const {
		pending: { selectedTreatmentMatrix },
		display,
		isAucOutdated,
		isLettersOutdated,
	} = useTrialAnalysis();

	const [disableColors, setDisableColors] = useState(false);
	const [showTreatmentNames, setShowTreatmentNames] = useState(false);
	const [groupPlots, setGroupPlots] = useState(display.isRCBD);

	const chartLoading = !aucData?.length;

	const { chartOptions, bottomExtra } = useMemo(() => {
		if (!aucData?.length || !selectedTreatmentMatrix?.length)
			return { chartOptions: { data: [], layout: {} }, bottomExtra: 0 };

		const sorted = _.sortBy(
			_.filter(aucData, (auc) => selectedTreatmentMatrix[auc.trialTreatmentId]),
			"trialTreatmentId",
		);

		// dense parallel arrays — index matches Plotly's 0-based category position
		const { boxX, boxMin, boxQ1, boxMedian, boxQ3, boxMax } = !groupPlots
			? {
					boxX: sorted.map((trt) => String(trt.trialTreatmentId)),
					boxMin: sorted.map((trt) => trt.ungroupedBoxStats.min),
					boxQ1: sorted.map((trt) => trt.ungroupedBoxStats.q1),
					boxMedian: sorted.map((trt) => trt.ungroupedBoxStats.median),
					boxQ3: sorted.map((trt) => trt.ungroupedBoxStats.q3),
					boxMax: sorted.map((trt) => trt.ungroupedBoxStats.max),
				}
			: {
					boxX: sorted.map((trt) => String(trt.trialTreatmentId)),
					boxMin: sorted.map((trt) => trt.groupedBoxStats.min),
					boxQ1: sorted.map((trt) => trt.groupedBoxStats.q1),
					boxMedian: sorted.map((trt) => trt.groupedBoxStats.median),
					boxQ3: sorted.map((trt) => trt.groupedBoxStats.q3),
					boxMax: sorted.map((trt) => trt.groupedBoxStats.max),
				};

		const scatterPlots: Record<number, { x: string; y: number; name: string; treatmentNum: number }[]> = {};

		for (const trt of sorted) {
			const xLabel = String(trt.trialTreatmentId);

			// use plot groups if plots are grouped otherwise spread each plot
			const units = groupPlots
				? trt.plotGroupAucs
				: _.flatMap(trt.plotGroupAucs, (pga) => {
						const { plotAucs, ...rest } = pga;
						return _.map(plotAucs, (pa) => ({ ...rest, meanAuc: pa.auc }));
					});

			for (const unit of units) {
				if (!scatterPlots[unit.plotReplicate]) scatterPlots[unit.plotReplicate] = [];
				scatterPlots[unit.plotReplicate].push({
					x: xLabel,
					y: unit.meanAuc,
					name: unit.plotGroup ?? "",
					treatmentNum: trt.trialTreatmentId,
				});
			}
		}

		// build a stats map keyed by x-label for use in the hover tooltip
		const statsMap: Record<string, { min: string; q1: string; median: string; q3: string; max: string }> = {};
		sorted.forEach((trt) => {
			const xLabel = String(trt.trialTreatmentId);
			statsMap[xLabel] = {
				min: (groupPlots ? trt.groupedBoxStats.min : trt.ungroupedBoxStats.min).toFixed(5),
				q1: (groupPlots ? trt.groupedBoxStats.q1 : trt.ungroupedBoxStats.q1).toFixed(5),
				median: (groupPlots ? trt.groupedBoxStats.median : trt.ungroupedBoxStats.median).toFixed(5),
				q3: (groupPlots ? trt.groupedBoxStats.q3 : trt.ungroupedBoxStats.q3).toFixed(5),
				max: (groupPlots ? trt.groupedBoxStats.max : trt.ungroupedBoxStats.max).toFixed(5),
			};
		});

		// get letters for display above the chart
		const letters = sorted.map((trt) =>
			aucMeanComparisons
				? (aucMeanComparisons.letteringPairs.find((lp) => lp.treatmentId === trt.trialTreatmentId)?.letter ?? null)
				: null,
		);
		// if all non-null letters are "a", replace all with "-"
		const allLettersA = letters.every((l) => l && l === "a");
		const displayLetters = allLettersA ? letters.map(() => "-") : letters;

		// annotation x uses the 0-based index into sorted, which matches Plotly's category positions
		const letterAnnotations: Partial<Annotations>[] =
			!isAucOutdated && !isLettersOutdated
				? sorted.map((_, idx) => ({
						x: idx,
						y: 1.06,
						yref: "paper",
						text: displayLetters[idx] ?? "",
						showarrow: false,
						font: { size: 14 },
						textangle: "-45",
					}))
				: [];

		const numTreatments = letterAnnotations.length;
		const maxLabelLen = Math.max(1, ...letterAnnotations.map((a) => (a.text ?? "").trim().length));
		const tooMany = maxLabelLen * numTreatments > 140;
		if (tooMany) {
			letterAnnotations.forEach((a) => {
				a.text = "-";
			});
		}
		const annotationY = tooMany ? 1.06 : 1.06 + Math.max(0, (maxLabelLen - 2) * 0.015);
		const marginTop = tooMany ? 60 : 60 + Math.max(0, (maxLabelLen - 2) * 10);
		letterAnnotations.forEach((a) => {
			a.y = annotationY;
		});

		const maxNameLength = showTreatmentNames
			? Math.max(0, ...sorted.map((trt) => (treatmentNames?.[trt.trialTreatmentId] ?? "").length))
			: 0;

		const nameAnnotations: Partial<Annotations>[] = showTreatmentNames
			? sorted
					.map((trt, idx) => {
						const name = treatmentNames?.[trt.trialTreatmentId];
						if (!name) return null!;
						return {
							x: idx,
							y: -0.12,
							yref: "paper" as const,
							xanchor: "center" as const,
							yanchor: "top" as const,
							text: name,
							showarrow: false,
							font: { size: 11 },
						};
					})
					.filter(Boolean)
			: [];

		const boxBaseProps = {
			type: "box",
			x: boxX,
			boxpoints: false,
			showlegend: false,
		};

		const traces: object[] = [
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxQ1,
				q3: boxMedian,
				upperfence: boxMedian,
				fillcolor: BOX_LOWER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMedian,
				q1: boxMedian,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: BOX_UPPER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: "black" },
				hoverinfo: "skip",
			},
		];

		for (const [rep, sp] of Object.entries(scatterPlots)) {
			const marker = { color: getColorFromPalette(!disableColors ? Number(rep) - 1 : 0), shape: "circle" };
			traces.push({
				type: "box",
				boxpoints: "all",
				jitter: groupPlots ? 0 : 0.5,
				pointpos: 0,
				fillcolor: "rgba(255,255,255, 0)",
				line: { color: "rgba(255,255,255, 0)" },
				name: `${rep}`,
				x: sp.map((p) => p.x),
				y: sp.map((p) => p.y),
				customdata: sp.map((p) => {
					const s = statsMap[p.x];
					return [p.name, p.treatmentNum, rep, s.min, s.q1, s.median, s.q3, s.max];
				}),
				hovertemplate:
					"<b>Plot: </b>%{customdata[0]}<br>" +
					"<b>Treatment: </b>%{customdata[1]}<br>" +
					"<b>Replicate: </b>%{customdata[2]}<br>" +
					"<b>Value: </b>%{y}<br>" +
					"<br>" +
					"<b>Min: </b>%{customdata[3]}<br>" +
					"<b>Q1: </b>%{customdata[4]}<br>" +
					"<b>Median: </b>%{customdata[5]}<br>" +
					"<b>Q3: </b>%{customdata[6]}<br>" +
					"<b>Max: </b>%{customdata[7]}<extra></extra>",
				marker: {
					color: marker.color,
					symbol: marker.shape,
					size: groupPlots ? 8 : 4,
					line: { color: "black", width: 0.5 },
				},
				showlegend: !disableColors,
			});
		}

		const bottomExtra = showTreatmentNames ? 50 + maxNameLength * 5 : 0;

		const layout: Partial<Layout> = {
			title: { text: "Area Under the Curve (AUC)", x: 0.01, xanchor: "left", font: { size: CHART_TITLE_SIZE } },
			xaxis: {
				title: {
					text: "Treatment",
					standoff: showTreatmentNames ? 80 + maxNameLength * 5 : 12,
					font: { size: AXIS_TITLE_SIZE },
				},
				type: "category",
				tickfont: { size: AXIS_TICK_SIZE },
				tickangle: 0,
				automargin: true,
			},
			yaxis: {
				title: { text: "AUC " + analysisName, standoff: 10, font: { size: AXIS_TITLE_SIZE } },
				tickfont: { size: AXIS_TICK_SIZE },
				zeroline: false,
				automargin: true,
			},
			legend: {
				orientation: "h",
				title: { text: "Replicates" },
				itemsizing: "constant",
				x: 1,
				xanchor: "right",
				y: showTreatmentNames ? -(0.18 + maxNameLength * 0.009) : -0.25,
				yanchor: "top",
			},
			margin: { l: 60, r: 20, b: 80 + bottomExtra, t: marginTop, pad: 0 },
			hovermode: "closest",
			autosize: true,
			annotations: [...(groupPlots ? letterAnnotations : []), ...nameAnnotations],
			boxmode: "overlay",
			// @ts-expect-error plotly types suck!
			boxgap: 0,
		};

		return { chartOptions: { data: traces, layout }, bottomExtra };
	}, [
		aucData,
		selectedTreatmentMatrix,
		disableColors,
		groupPlots,
		isAucOutdated,
		isLettersOutdated,
		showTreatmentNames,
		analysisName,
		aucMeanComparisons,
		treatmentNames,
	]);

	const modeBarButtons = useMemo(() => {
		const buttons: ModeBarButtonAny[] = [];
		if (treatmentNames) {
			buttons.push({
				name: "toggle treatment names",
				title: "Toggle Treatment Names",
				icon: PlotlyIcons.title,
				attr: showTreatmentNames ? "names-active" : "names-inactive",
				click: () => {
					setShowTreatmentNames(!showTreatmentNames);
				},
			});
		}

		if (entityNames?.fieldScaleTrial) {
			buttons.push({
				name: "toggle treatment blocks",
				title: "Toggle Treatment Blocks",
				icon: PlotlyIcons.treatmentBlocks,
				attr: groupPlots ? "blocks-active" : "blocks-inactive",
				click: () => {
					setGroupPlots(!groupPlots);
				},
			});
		}

		buttons.push({
			name: "toggle chart colors",
			title: "Toggle Chart Colors",
			icon: PlotlyIcons.pallette,
			attr: disableColors ? "colors-inactive" : "colors-active",
			click: () => {
				setDisableColors(!disableColors);
			},
		});
		return buttons;
	}, [disableColors, entityNames?.fieldScaleTrial, groupPlots, showTreatmentNames, treatmentNames]);

	return (
		<Segment
			basic
			loading={chartLoading}
			style={{
				borderRadius: "8px",
				border: "1px solid var(--border)",
				background: "#ffffffe6",
				margin: 0,
				position: "relative",
			}}
		>
			{!aucData?.length ? (
				<p>There is no data selected for the box and whisker chart.</p>
			) : (
				<>
					<AFPlot
						data={chartOptions.data}
						layout={chartOptions.layout}
						config={{
							displayModeBar: true,
							modeBarButtonsToAdd: modeBarButtons,
							toImageButtonOptions: {
								filename: `${entityNames?.trialName}_${/\(([^)]+)\)/.exec(analysisName)?.[1] ?? analysisName}_BoxWhisker`,
							},
						}}
						useResizeHandler
						style={{ width: "100%", height: bottomExtra > 0 ? `calc(400px + ${bottomExtra}px)` : "400px" }}
					/>
				</>
			)}
		</Segment>
	);
};

export default TimecourseBoxWhiskerChart;
